import {
	Button,
	Checkbox,
	FormControlLabel,
	MenuItem,
	TextField,
} from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import {
	Card,
	CardBody,
	CardHeader,
	CardHeaderToolbar,
} from '../../../../_metronic/_partials/controls'
import { getClubs } from '../../../../api/club'
import { ensayarAlta, provisionarClub, simular } from '../../../../api/contract'
import { alertError, alertSuccess } from '../../../../utils/logger'

/** El valor del desplegable cuando el club NO está en el catálogo. */
const UNO_NUEVO = '__nuevo__'

const euros = (centimos) =>
	new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format((centimos ?? 0) / 100)

/**
 * El alta de un club, en dos pasos: primero se ve QUÉ va a pasar y luego se
 * hace. Es el mismo `--escribir` de los scripts, y por la misma razón: esto
 * crea usuarios, entrega aparatos y manda un correo a alguien de fuera.
 *
 * El ensayo lo calcula el servidor, no esta pantalla. Aquí no hay ni una regla
 * de negocio: si la hubiera, algún día diría una cosa el panel y otra el
 * webhook de Stripe, que llama a la misma función que el botón de abajo.
 */
export default function AltaClubPage() {
	const [pedido, setPedido] = useState({
		club: '',
		nombre: '',
		correo: '',
		gestor: '',
		aparatos: 25,
		compromiso: true,
		piloto: false,
		enviarCorreo: true,
		nota: '',
	})
	const [clubes, setClubes] = useState([])
	const [cuota, setCuota] = useState(null)
	const [ensayo, setEnsayo] = useState(null)
	const [hecho, setHecho] = useState(null)
	const [trabajando, setTrabajando] = useState(false)
	const history = useHistory()

	useEffect(() => {
		// Los clubes ya están cargados desde antes de que ninguno supiera de
		// SailRadar. Aquí se ELIGE de esa lista; crear uno es la excepción.
		getClubs()
			.then((res) => {
				const lista = [...res.data].sort((a, b) => a.fullName.localeCompare(b.fullName, 'es'))
				setClubes(lista)
			})
			.catch((error) => alertError({ error, customMessage: 'No se pudieron cargar los clubes.' }))
	}, [])

	/**
	 * La cuota la calcula el SERVIDOR, no esta pantalla.
	 *
	 * Aquí había una copia de la regla —«sube al tramo que te cubra»— y se
	 * quedó vieja el día que se pudieron añadir aparatos sueltos sobre
	 * cualquier pack: enseñaba 399 € para 13 aparatos cuando el contrato que
	 * se iba a firmar decía 235 €. Un número de menos en una pantalla interna
	 * es un precio que se dice por teléfono y luego no cuadra con la factura.
	 */
	useEffect(() => {
		const n = Number(pedido.aparatos)
		if (pedido.piloto || !n || n < 1) {
			setCuota(null)
			return undefined
		}
		// Se escribe con el teclado: no hace falta preguntar por cada tecla.
		const t = setTimeout(() => {
			simular(n, pedido.compromiso)
				.then((res) => setCuota(res.data))
				.catch(() => setCuota(null))
		}, 300)
		return () => clearTimeout(t)
	}, [pedido.aparatos, pedido.piloto, pedido.compromiso])

	const cambiar = (campo) => (e) => {
		const valor = e.target.type === 'checkbox' ? e.target.checked : e.target.value
		setPedido((p) => ({ ...p, [campo]: valor }))
		// Cualquier cambio invalida el ensayo: enseñar un plan viejo junto a
		// datos nuevos es peor que no enseñar nada.
		setEnsayo(null)
	}

	/** La referencia es lo que impide que un alta repetida duplique nada. */
	const referencia = pedido.correo ? `panel:${pedido.correo.trim().toLowerCase()}` : null

	/** Lo que entiende el backend: `compromiso` o `mensual`. */
	const conTipo = () => ({ ...pedido, tipo: pedido.compromiso ? 'compromiso' : 'mensual', referencia })

	function verQuePasaria() {
		setTrabajando(true)
		ensayarAlta(conTipo())
			.then((res) => setEnsayo(res.data))
			.catch((error) => alertError({ error, customMessage: 'No se pudo calcular el alta.' }))
			.finally(() => setTrabajando(false))
	}

	function darDeAlta() {
		setTrabajando(true)
		provisionarClub(conTipo())
			.then((res) => {
				setHecho(res.data)
				alertSuccess({
					title: 'Club dado de alta',
					customMessage: res.data.yaHecho ? 'Ya estaba hecho: no se ha tocado nada.' : 'Todo listo.',
				})
			})
			.catch((error) => alertError({ error, customMessage: 'No se pudo dar de alta el club.' }))
			.finally(() => setTrabajando(false))
	}

	// ── Ya está hecho: lo único que importa ahora es la contraseña ────────
	if (hecho && !hecho.yaHecho)
		return (
			<Card>
				<CardHeader title={`«${hecho.club.fullName}» dado de alta`} />
				<CardBody>
					<p>
						Gestor: <strong>{hecho.usuario.email}</strong>
					</p>
					{hecho.contrasena && (
						<div
							style={{
								background: '#fff8e1',
								border: '1px solid #ffe082',
								borderRadius: 8,
								padding: 16,
								margin: '16px 0',
							}}
						>
							<p style={{ margin: 0 }}>
								Contraseña:{' '}
								<strong style={{ fontFamily: 'ui-monospace, monospace', fontSize: 18 }}>
									{hecho.contrasena}
								</strong>
							</p>
							<p style={{ margin: '8px 0 0', color: '#8a6d1b' }}>
								No se guarda en claro en ningún sitio. Apúntala ahora o habrá que restablecerla.
							</p>
						</div>
					)}
					<p>
						Aparatos entregados: <strong>{hecho.entregados}</strong>
						{hecho.pendientes > 0 && (
							<span style={{ color: '#c0392b' }}> · {hecho.pendientes} pendientes de pedir al proveedor</span>
						)}
					</p>
					<p>
						Bienvenida:{' '}
						{hecho.correoEnviado ? (
							'enviada'
						) : (
							<strong style={{ color: '#c0392b' }}>NO enviada — mándala a mano</strong>
						)}
					</p>
					<Button variant="outlined" onClick={() => history.push('/contracts')}>
						Ver los contratos
					</Button>
				</CardBody>
			</Card>
		)


	return (
		<Card>
			<CardHeader title="Dar de alta un club">
				<CardHeaderToolbar>
					<button type="button" className="btn btn-light" onClick={() => history.push('/contracts')}>
						Volver
					</button>
				</CardHeaderToolbar>
			</CardHeader>
			<CardBody>
				<TextField
					select
					label="Club"
					helperText={`${clubes.length} clubes en el catálogo`}
					value={pedido.club || (pedido.nombre ? UNO_NUEVO : '')}
					onChange={(e) => {
						const v = e.target.value
						// Una cosa o la otra, nunca las dos: si se elige del
						// catálogo no hay nombre que inventar, y al revés.
						setPedido((p) => ({
							...p,
							club: v === UNO_NUEVO ? '' : v,
							nombre: v === UNO_NUEVO ? p.nombre : '',
						}))
						setEnsayo(null)
					}}
					margin="normal"
					fullWidth
				>
					{clubes.map((c) => (
						<MenuItem key={c._id} value={c._id}>
							{c.fullName}
						</MenuItem>
					))}
					<MenuItem value={UNO_NUEVO}>— No está en la lista: crear uno nuevo —</MenuItem>
				</TextField>

				{!pedido.club && (
					<TextField
						label="Nombre del club nuevo"
						helperText="Tal cual va a salir publicado en sailradar.com. Mira antes que no esté ya con otro nombre."
						value={pedido.nombre}
						onChange={cambiar('nombre')}
						margin="normal"
						fullWidth
					/>
				)}
				<TextField
					label="Correo del gestor"
					helperText="Será su usuario para entrar en live.sailradar.com"
					value={pedido.correo}
					onChange={cambiar('correo')}
					margin="normal"
					fullWidth
				/>
				<TextField
					label="Nombre del gestor"
					helperText="Si se deja vacío se usa el del club"
					value={pedido.gestor}
					onChange={cambiar('gestor')}
					margin="normal"
					fullWidth
				/>
				<TextField
					label="Aparatos"
					type="number"
					value={pedido.aparatos}
					onChange={cambiar('aparatos')}
					margin="normal"
					fullWidth
					helperText={
						pedido.piloto
							? 'Sin cuota: es un piloto'
							: cuota
								? `${euros(cuota.monthlyCents)}/mes + IVA · primera factura ${euros(cuota.firstInvoiceCents)}`
								: ' '
					}
				/>
				<TextField
					label="Nota"
					value={pedido.nota}
					onChange={cambiar('nota')}
					margin="normal"
					fullWidth
				/>

				<FormControlLabel
					control={<Checkbox checked={pedido.compromiso} onChange={cambiar('compromiso')} />}
					label="Compromiso de 12 meses: tres de regalo repartidos en la cuota y alta a mitad de precio"
				/>
				<br />
				<FormControlLabel
					control={<Checkbox checked={pedido.piloto} onChange={cambiar('piloto')} />}
					label="Piloto: colabora sin pagar, sin cuota ni compromiso"
				/>
				<br />
				<FormControlLabel
					control={<Checkbox checked={pedido.enviarCorreo} onChange={cambiar('enviarCorreo')} />}
					label="Mandarle el correo de bienvenida con su acceso"
				/>

				<div style={{ marginTop: 24 }}>
					<Button
						variant="outlined"
						onClick={verQuePasaria}
						disabled={trabajando || !pedido.correo || (!pedido.club && !pedido.nombre)}
					>
						Ver qué pasaría
					</Button>
				</div>

				{ensayo && (
					<div style={{ marginTop: 24, background: '#f4f7fa', borderRadius: 8, padding: 16 }}>
						{!ensayo.ok && (
							<>
								<strong style={{ color: '#c0392b' }}>No se puede dar de alta:</strong>
								<ul>
									{ensayo.problemas.map((p) => (
										<li key={p}>{p}</li>
									))}
								</ul>
							</>
						)}

						{ensayo.ok && ensayo.yaHecho && (
							<strong>Esta alta ya está hecha. No se tocaría nada.</strong>
						)}

						{ensayo.ok && !ensayo.yaHecho && (
							<>
								<strong>Se haría esto:</strong>
								<ul>
									{ensayo.acciones.map((a, i) => (
										<li key={i}>{textoDe(a)}</li>
									))}
								</ul>
								{ensayo.avisos.length > 0 && (
									<ul style={{ color: '#8a6d1b' }}>
										{ensayo.avisos.map((a) => (
											<li key={a}>{a}</li>
										))}
									</ul>
								)}
								<Button
									variant="contained"
									color="primary"
									onClick={darDeAlta}
									disabled={trabajando}
									style={{ marginTop: 12 }}
								>
									Dar de alta
								</Button>
							</>
						)}
					</div>
				)}
			</CardBody>
		</Card>
	)
}

function textoDe(a) {
	switch (a.tipo) {
	case 'crearClub':
		return `Crear el club «${a.nombre}» (dirección /${a.slug}/)`
	case 'crearGestor':
		return `Crear la cuenta de gestor ${a.correo}`
	case 'crearContrato':
		return `Guardar el contrato de ${a.aparatos} aparatos`
	case 'entregarAparatos':
		return `Entregarle ${a.cuantos} aparatos`
	case 'darLaBienvenida':
		return `Mandarle la bienvenida a ${a.correo}`
	default:
		return a.tipo
	}
}
